/**
 * tourSteps — role-aware step definitions for the first-login onboarding
 * tour (GH issue #4). Steps derive from the same RoleAwareNav data that
 * drives the sidebar, so a role only ever sees steps for sections it can
 * actually access (e.g. TMs never see the Doubts Inbox step).
 *
 * Step shape:
 *   id           — stable key
 *   target       — data-tour attribute value to spotlight (null = centred card)
 *   mobileTarget — fallback target tried when `target` isn't visible
 *                  (e.g. the sidebar lives in a closed drawer on mobile)
 *   placement    — preferred popover side: 'right' | 'bottom' | 'left' | 'top'
 *   segment      — route segment the step lives on (e.g. 'job-sheet');
 *                  the tour navigates to `${rolePrefix}/${segment}` and
 *                  resumes there, so it can walk real workflows — creating
 *                  tasks, working the task table, answering doubts.
 */

import { getNavItems } from './RoleAwareNav.jsx';

export const TOUR_START_EVENT = 'orin:tour-start';

/** Ask the mounted OnboardingTour (in AppShell) to replay the tour. */
export function startOnboardingTour() {
  window.dispatchEvent(new CustomEvent(TOUR_START_EVENT));
}

export function buildTourSteps(role) {
  const { workspace } = getNavItems(role);
  const sectionNames = workspace.map((item) => item.label).join(', ');
  const has = (id) => workspace.some((item) => item.id === id && !item.disabled);

  const steps = [
    {
      id: 'welcome',
      target: null,
      title: 'Welcome to ORIN 👋',
      body:
        'Here is a quick tour of your workspace — it takes under a minute, ' +
        'and you can skip it at any time.',
    },
    {
      id: 'sidebar',
      target: 'sidebar',
      mobileTarget: 'nav-toggle',
      placement: 'right',
      title: 'Main navigation',
      body: `This is your main navigation. Use it to access ${sectionNames}.`,
    },
  ];

  if (has('dashboard')) {
    steps.push({
      id: 'nav-dashboard',
      target: 'nav-dashboard',
      placement: 'right',
      title: 'Dashboard',
      body:
        'Your home screen. See active tasks, overdue items and performance ' +
        'at a glance.',
    });
  }

  if (has('job-sheet')) {
    steps.push({
      id: 'add-task',
      target: 'add-task',
      segment: 'job-sheet',
      placement: 'bottom',
      title: 'Create a task',
      body:
        'This is your Job Sheet, and new tasks start here. Give the task a ' +
        'title, event, priority and closure date — it appears in your list ' +
        'instantly.',
    });
    steps.push({
      id: 'task-table',
      target: 'task-table',
      segment: 'job-sheet',
      placement: 'top',
      title: 'Work your tasks',
      body:
        'Your daily tasks live in this table. Open any task to start its ' +
        'timer, update the status, add comments — and raise a doubt if you ' +
        'are stuck. Doubts go straight to the admins.',
    });
  }

  if (has('doubts')) {
    steps.push({
      id: 'doubts-inbox',
      target: 'doubts-inbox',
      segment: 'doubts',
      placement: 'bottom',
      title: 'Answer doubts',
      body:
        'Doubts raised by the team arrive in this inbox in real time. Open ' +
        'one to respond — your answer goes back to the task owner and marks ' +
        'the doubt resolved.',
    });
  }

  if (has('reports')) {
    steps.push({
      id: 'nav-reports',
      target: 'nav-reports',
      placement: 'right',
      title: 'Reports',
      body: 'Track time logs and team performance from here.',
    });
  }

  steps.push({
    id: 'settings',
    target: 'settings',
    placement: 'bottom',
    title: 'Settings',
    body:
      'Customise your appearance — theme, accent colour, font size and ' +
      'table density — from here.',
  });

  steps.push({
    id: 'profile',
    target: 'profile',
    placement: 'right',
    title: 'Your profile',
    body:
      'Your account lives here — open it to update your details. ' +
      'That is it, you are all set! 🎉',
  });

  return steps;
}
